/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Alert } from '@mui/material';
import { ApiService } from '../../services/apiService';
import { ApiProductivityResponse } from '../../types/api';
import { ProductivityChartView } from './ChartView';

const apiService = new ApiService();

// cols days
// [2021-03-22],[2021-03-23],[2021-03-24],[2021-03-25],[2021-03-26],[2021-03-27],[2021-03-29],[2021-03-30],[2021-03-31],[2021-04-01],[2021-04-02],[2021-04-03],[2021-04-05],[2021-04-07],[2021-04-08],[2021-04-09],[2021-04-10]
// cols weeks
// "22/03/2021 - 28/03/2021|29/03/2021 - 04/04/2021|05/04/2021 - 11/04/2021"

const getDateFromColsWeek = (cols: string) => {
  const colDays = cols?.split('|') || [];
  const totalCols = colDays.length;
  if (!totalCols) {
    return {
      start: '',
      end: '',
    }
  }
  const start = colDays[0].split(' - ')[0];
  const end = colDays[totalCols - 1].split(' - ')[1];
  return {
    start,
    end,
  }
}

export const ProductivityChartLoader: React.FC<{hidden?: boolean}> = ({hidden}) => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<ApiProductivityResponse | null>(null);
  const [dataByDay, setDataByDay] = useState<ApiProductivityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: ''
  });

  const token = searchParams.get('token') || '';
  const moduleId = searchParams.get('moduleId') || '';
  const activityId = parseInt(searchParams.get('activityId') || '');
  const [period, setPeriod] = useState<'week' | 'day' | 'all' | 'year'>('week');

  useEffect(() => {
    if (hidden) {
      return;
    }
    if (period === 'week' && !data) {
      loadData();
    } else if (period === 'day' && !dataByDay) {
      loadDataByDays();
    }
  }, [token, moduleId, activityId, period, hidden]);

  useEffect(() => {
    if (hidden || !data || startDate) {
      return;
    }
    const range = getDateFromColsWeek(data.Weeks as any);
    setStartDate(range.start);
    setEndDate(range.end)
    setDateRange(range)
  }, [data]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!token || !moduleId) {
        throw new Error('Faltan parámetros requeridos: token y moduleId');
      }

      apiService.setAuth(token, moduleId);
      
      const response = await apiService.getProductivityData(activityId);
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const loadDataByDays = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!token || !moduleId) {
        throw new Error('Faltan parámetros requeridos: token y moduleId');
      }

      apiService.setAuth(token, moduleId);
      
      const response = await apiService.getProductivityByDaysData(activityId);
      setDataByDay(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  if (hidden) {
    return <></>
  }

  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        Cargando datos...
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Error: {error}
        </Alert>
      </Box>
    );
  }
  
  const finalData = period === 'week' ? data : dataByDay;

  if (!finalData) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">No se encontraron datos</Alert>
      </Box>
    );
  }

  return (
    <ProductivityChartView
      data={finalData} 
      period={period}
      onPeriodChange={setPeriod}
      dateRange={dateRange}
      setDateRange={setDateRange}
      startDate={startDate}
      endDate={endDate}
    />
  );
};